import { type ReactElement, cloneElement, useId } from 'react';
import type { FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

/** How to word a validation error for this field (Zod messages are not shown directly). */
export type FieldKind = 'text' | 'email' | 'password' | 'passwordConfirm' | 'currency' | 'timezone';

function messageKey(error: FieldError, kind: FieldKind): string {
  if (error.type === 'too_big') return 'validation.tooLong';
  if (kind === 'email' && error.type !== 'too_small') return 'validation.email';
  if (kind === 'password' && error.type === 'too_small') return 'validation.passwordLength';
  if (kind === 'passwordConfirm' && error.type === 'custom') return 'validation.passwordMismatch';
  if (kind === 'currency') return 'validation.currency';
  if (kind === 'timezone') return 'validation.timezone';
  if (error.type === 'too_small' || error.type === 'invalid_type') return 'validation.required';
  return 'validation.invalid';
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: FieldError;
  kind?: FieldKind;
  children: ReactElement<Record<string, unknown>>;
}

/** Label, control, hint and error, wired together for screen readers. */
export function Field({ label, hint, error, kind = 'text', children }: FieldProps) {
  const { t } = useTranslation();
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') || undefined;
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[13px] font-medium text-muted">
        {label}
      </label>
      {cloneElement(children, {
        id,
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
      })}
      {hint && (
        <p id={hintId} className="text-xs text-muted">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="text-sm text-danger">
          {t(messageKey(error, kind))}
        </p>
      )}
    </div>
  );
}
